import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Form,
  Row,
  Col,
  ButtonToolbar,
  Button,
  Card,
  Spinner,
} from "react-bootstrap";
import * as SubmitRecipe from "./subComponents/submitRecipe";
import type { GalleryItem } from "./subComponents/submitRecipe/GalleryInput";
import type { OwnerOption } from "./subComponents/submitRecipe/OwnerInput";
import { common } from "../js/common";

interface RecipeResponse {
  id: number;
  name: string;
  description: string;
  creatorId: number;
  creatorName?: string;
  previewImageUrl: string | null;
  galleryImages: { id: number; url: string; sortOrder: number }[] | null;
}

function SubmitPage() {
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isEdit = editId !== null && editId !== "";

  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [removedImageIds, setRemovedImageIds] = useState<number[]>([]);
  const [image, setImage] = useState<File | null>(null);
  const [existingPreview, setExistingPreview] = useState<string | null>(null);
  const [removePreview, setRemovePreview] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(isEdit);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  // Owner assignment (admins only): the account list, the chosen owner, and
  // the owner the recipe had when loaded (so we only send a real change).
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [accounts, setAccounts] = useState<OwnerOption[]>([]);
  const [ownerId, setOwnerId] = useState<number | null>(null);
  const [ownerName, setOwnerName] = useState<string>("");
  const [loadedOwnerId, setLoadedOwnerId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sessionRes = await common.fetchRestAPI("sessions", "GET");
        if (!sessionRes.ok) return;
        const session = await sessionRes.json();
        if (cancelled || session?.role !== "admin") return;
        setIsAdmin(true);
        if (!isEdit) {
          // a new recipe defaults to the admin themself
          setOwnerId(session.id);
          setLoadedOwnerId(session.id);
        }
        const accountsRes = await common.fetchRestAPI("accounts", "GET");
        if (!accountsRes.ok) return;
        const body = await accountsRes.json();
        if (!cancelled) setAccounts((body.accounts ?? []).map(({ id, userName }: OwnerOption) => ({ id, userName })));
      } catch {
        // the owner selector is optional; the form still works without it
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await common.fetchRestAPI(`recipes/${editId}`, "GET");
        if (!res.ok) {
          throw new Error(res.status === 404 ? "Recipe not found" : "Failed to load recipe");
        }
        const recipe: RecipeResponse = await res.json();
        if (cancelled) return;
        setTitle(recipe.name);
        setDescription(recipe.description);
        setExistingPreview(recipe.previewImageUrl);
        setOwnerId(recipe.creatorId);
        setOwnerName(recipe.creatorName ?? "");
        setLoadedOwnerId(recipe.creatorId);
        setGallery(
          [...(recipe.galleryImages ?? [])]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(({ id, url }) => ({ key: `existing-${id}`, kind: "existing", id, url })),
        );
      } catch (err: any) {
        if (!cancelled) setError(err.message || "Failed to load recipe");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, editId]);

  // "Use as preview image" on a gallery image. A newly added file is reused
  // as-is; an existing (already uploaded) image is fetched into a File so it
  // is sent as `preview_image` on save — the API keeps its own copy.
  const useGalleryAsPreview = async (item: GalleryItem) => {
    if (item.kind === "new") {
      setImage(item.file);
      return;
    }
    try {
      const res = await fetch(item.url, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const name = item.url.split("/").pop() || `gallery-${item.id}`;
      setImage(new File([blob], name, { type: blob.type }));
    } catch {
      setError("Couldn't use that image as the preview. Please try again.");
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setError("");
    setSuccess("");
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("name", title);
      formData.append("description", description);
      // Admins can assign the recipe to another account. Only sent when it
      // differs from the current owner (or, on create, from the admin).
      if (isAdmin && ownerId !== null && ownerId !== loadedOwnerId) {
        formData.append("creator_id", String(ownerId));
      }
      // A new preview replaces the old one; otherwise, on edit, an explicit
      // removal of the existing preview is sent as a flag.
      if (image) formData.append("preview_image", image);
      else if (isEdit && removePreview) formData.append("delete_preview_image", "true");
      // New files go in display order; on edit, gallery_order gives the final
      // order across existing ids and "new:<i>" uploads, and removed existing
      // images are deleted.
      const newItems = gallery.filter((item) => item.kind === "new");
      newItems.forEach((item) => {
        if (item.kind === "new") formData.append("gallery_images", item.file);
      });
      if (isEdit) {
        gallery.forEach((item) => {
          formData.append(
            "gallery_order",
            item.kind === "existing" ? String(item.id) : `new:${newItems.indexOf(item)}`,
          );
        });
        removedImageIds.forEach((id) => formData.append("delete_gallery_images", String(id)));
      }

      const response = isEdit
        ? await common.fetchRestAPI(`recipes/${editId}`, "PATCH", formData)
        : await common.fetchRestAPI("recipes", "POST", formData);

      if (!response.ok) {
        if (response.status === 413) {
          throw new Error("The uploaded images are too large. Please use smaller files (max ~20MB total).");
        }
        if (response.status === 403) {
          throw new Error("You don't have permission to edit this recipe.");
        }
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || response.statusText || (isEdit ? "Failed to update recipe" : "Failed to create recipe"));
      }

      let targetId: number | string | null = isEdit ? editId : null;
      if (!isEdit) {
        const body = await response.json().catch(() => null);
        targetId = body?.data?.id ?? null;
      }
      setSuccess(isEdit ? "Recipe updated!" : "Recipe created!");

      await new Promise((resolve) => setTimeout(resolve, 1500));
      window.location.href = targetId ? `/recipe/${targetId}` : "/";
    } catch (err: any) {
      setError(err.message || "An error occurred submitting the recipe");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center p-5">
        <Spinner animation="border" size="sm" role="status" />
      </Container>
    );
  }

  return (
    <Container className="p-4">
      <Row className="justify-content-center">
        <Col lg={9}>
          <Form onSubmit={onSubmit}>
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <strong>{error}</strong>
                <button type="button" className="btn-close" onClick={() => setError("")} aria-label="Close"></button>
              </div>
            )}
            {success && (
              <div className="alert alert-success" role="alert">
                <strong>{success}</strong>
              </div>
            )}
            <Row className="align-items-center">
              <Col>
                <h2 className="mb-0">{isEdit ? "Edit Recipe" : "New Recipe"}</h2>
              </Col>
              <Col className="col-auto">
                <ButtonToolbar>
                  <Button className="mx-2" variant="secondary" href={isEdit ? `/recipe/${editId}` : "/"}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="success" disabled={submitting || (isEdit && !!error && !title)}>
                    {submitting ? "Saving..." : "Save"}
                  </Button>
                </ButtonToolbar>
              </Col>
            </Row>
            <Row>
              <Col>
                <Card className="mt-2 p-5">
                  <Row className="justify-content-end align-items-end mb-3">
                    <Col className="col-12 text-center" sm={2}>
                      <SubmitRecipe.ThumbnailInput
                        image={image}
                        setImage={setImage}
                        existingUrl={existingPreview}
                        onRemoveExisting={() => {
                          setExistingPreview(null);
                          setRemovePreview(true);
                        }}
                      ></SubmitRecipe.ThumbnailInput>
                    </Col>
                    <Col className="">
                      <SubmitRecipe.TitleInput
                        value={title}
                        setTitle={setTitle}
                      ></SubmitRecipe.TitleInput>
                    </Col>
                    <Col className="col-auto">
                      <SubmitRecipe.AIButton></SubmitRecipe.AIButton>
                    </Col>
                  </Row>
                  {isAdmin && (
                    <Row className="mb-3">
                      <Col sm={4}>
                        <SubmitRecipe.OwnerInput
                          value={ownerId}
                          accounts={accounts}
                          currentName={ownerName}
                          onChange={setOwnerId}
                        ></SubmitRecipe.OwnerInput>
                      </Col>
                    </Row>
                  )}
                  <Row className="mb-3">
                    <Col>
                      <SubmitRecipe.BodyInput
                        value={description}
                        setDescription={setDescription}
                      ></SubmitRecipe.BodyInput>
                    </Col>
                  </Row>
                  <SubmitRecipe.GalleryInput
                    items={gallery}
                    onChange={setGallery}
                    onRemoveExisting={(id) => setRemovedImageIds((prev) => [...prev, id])}
                    onUseAsPreview={useGalleryAsPreview}
                    onOcrText={(text) =>
                      setDescription((prev) =>
                        prev.trim() ? `${prev.replace(/\s+$/, "")}\n\n${text.trim()}` : text.trim(),
                      )
                    }
                    onError={setError}
                  ></SubmitRecipe.GalleryInput>
                </Card>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </Container>
  );
}

export default SubmitPage;
